import {
  AfterViewInit,
  ApplicationRef,
  Component,
  ComponentRef,
  DestroyRef,
  ElementRef,
  EnvironmentInjector,
  TemplateRef,
  Type,
  computed,
  createComponent,
  inject,
  input,
  output,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { AppButtonComponent } from '../app-button/app-button.component';
import { NotificationBarComponent } from '../notification-bar/notification-bar.component';

import { PlatformService } from '../../services/platform-service/platform.service';
import { ScrollService } from '../../services/scroll-service/scroll.service';
import { ErrorSignalService } from '../error-page/services/error-signal.service';

import { ButtonTypeEnum } from '../../enums/buttonType.enum';
import { DragDropStateService } from '../../services/drag-and-drop-service/drag-and-drop-state.service';

@Component({
  selector: 'dialog-popup',
  imports: [CommonModule, AppButtonComponent, NotificationBarComponent],
  templateUrl: './dialog-popup.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./dialog-popup.component.scss'],
})
export class DialogPopupComponent implements AfterViewInit {
  protected readonly ButtonTypeEnum = ButtonTypeEnum;
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly appRef = inject(ApplicationRef);
  private readonly destroyRef = inject(DestroyRef);

  private floatingComponentRef?: ComponentRef<unknown>;
  private pointerDownHandler?: (event: PointerEvent) => void;

  readonly dialogRef = viewChild.required<ElementRef<HTMLDialogElement>>('dialog');

  readonly dialogContentRef = viewChild<ElementRef<HTMLDivElement>>('dialogContent');

  readonly floatingDialogRef = viewChild.required<ElementRef<HTMLElement>>('floatingDialog');

  readonly scrollableContent = input(false);

  readonly allowOverflow = input(false);
  readonly isPrimaryButtonEnabled = input(true);
  readonly isSecondaryButtonEnabled = input(true);
  readonly displayWarningIcon = input(false);
  readonly enableScrollWhenOverFlow = input(false);
  readonly isInfoDialog = input(false);

  readonly primaryButtonDisabled = input(false);
  readonly secondaryButtonDisabled = input(false);
  readonly tertiaryButtonDisabled = input(false);

  readonly primaryButtonName = input('');
  readonly secondaryButtonName = input('');
  readonly tertiaryButtonName = input('');

  readonly isTertiaryButtonEnabled = input(false);

  readonly title = input.required<string>();
  readonly mobileTitle = input<string>();

  readonly showCloseButton = input<boolean | undefined>(undefined);
  readonly contentCenter = input(false);

  readonly buttonsTemplate = input<TemplateRef<unknown>>();

  readonly dialogEscapeCloseClicked = output<void>();
  readonly outsideContentClicked = output<void>();
  readonly primaryButtonClicked = output<void>();
  readonly secondaryButtonClicked = output<void>();
  readonly tertiaryButtonClicked = output<void>();

  protected readonly showErrorNotification = computed(() =>
    this.errorSignalService.showBookmarkPopupError(),
  );

  protected readonly platformService = inject(PlatformService);

  protected readonly shouldShowCloseButton = computed(
    () => this.showCloseButton() ?? this.platformService.isMobile(),
  );

  private readonly scrollService = inject(ScrollService);
  private readonly dragDropStateService = inject(DragDropStateService);
  private readonly errorSignalService = inject(ErrorSignalService);

  /**
   * Prevents native dialog drag behavior (especially in Edge) to keep it in the top layer.
   * Also listens for ESC key to close the dialog.
   */
  ngAfterViewInit(): void {
    const dialog = this.dialogRef().nativeElement;

    const cancelHandler = (): void => {
      this.dialogEscapeCloseClicked.emit();
    };

    dialog.addEventListener('cancel', cancelHandler);

    this.pointerDownHandler = (event: PointerEvent): void => {
      const target = event.target as HTMLElement;

      // Allow interaction with interactive elements
      // Check if target itself is an interactive element
      if (this.isInteractiveElement(target)) {
        return;
      }

      event.preventDefault(); // block drag/move
      event.stopImmediatePropagation();
    };

    dialog.addEventListener('pointerdown', this.pointerDownHandler, {
      capture: true, // run before browser’s internal pointer logic
    });

    this.destroyRef.onDestroy(() => {
      dialog.removeEventListener('cancel', cancelHandler);

      if (this.pointerDownHandler) {
        dialog.removeEventListener('pointerdown', this.pointerDownHandler, {
          capture: true,
        });
      }
    });
  }

  openDialog(): void {
    const hasScrollableContent = this.scrollableContent() || this.enableScrollWhenOverFlow();
    this.scrollService.disableScroll(
      hasScrollableContent ? this.dialogRef().nativeElement : undefined,
    );
    this.dragDropStateService.setDialogOpen(true);
    this.dialogRef().nativeElement.showModal();
  }

  closeDialog(): void {
    this.closeFloatingComponent();
    this.dialogRef().nativeElement.close();
    this.scrollService.enableScroll();
    this.dragDropStateService.setDialogOpen(false);
    this.errorSignalService.clearBookmarkError();
  }

  primaryButtonClickedMethod(event: MouseEvent): void {
    if (!this.isOpened()) {
      return;
    }

    this.stopPropagationAndPreventDefault(event);
    this.closeDialog();
    this.primaryButtonClicked.emit();
  }

  secondaryButtonClickedMethod(event: MouseEvent): void {
    if (!this.isOpened()) {
      return;
    }

    this.stopPropagationAndPreventDefault(event);
    this.closeDialog();
    this.secondaryButtonClicked.emit();
  }

  tertiaryButtonClickedMethod(event: MouseEvent): void {
    if (!this.isOpened()) {
      return;
    }

    this.stopPropagationAndPreventDefault(event);
    this.tertiaryButtonClicked.emit();
  }

  stopPropagationAndPreventDefault(event: MouseEvent): void {
    this.stopPropagation(event);
    event.preventDefault();
  }

  stopPropagation(event: MouseEvent): void {
    event.stopPropagation();
  }

  backgroundClick(event: MouseEvent): void {
    if (!this.isOpened()) {
      return;
    }

    if (this.floatingDialogRef().nativeElement.matches(':popover-open')) {
      return;
    }

    this.outsideContentClicked.emit();

    if (!this.platformService.isMobile()) {
      this.closeDialog();
      this.stopPropagationAndPreventDefault(event);
    }
  }

  private isOpened(): boolean {
    return this.dialogRef().nativeElement.open;
  }

  // TODO: refactor the floating popover out of DialogPopupComponent. Hosting it
  // here (a popover living inside the dialog frame, positioned by hand) couples a
  // generic dialog to the emoji picker's needs.
  openFloatingComponent<T>(component: Type<T>, _anchor: HTMLElement): ComponentRef<T> {
    const floating = this.floatingDialogRef().nativeElement;

    const componentRef = createComponent(component, {
      environmentInjector: this.environmentInjector,
    });

    this.floatingComponentRef = componentRef;

    this.appRef.attachView(componentRef.hostView);

    floating.appendChild(componentRef.location.nativeElement);

    floating.showPopover();

    this.positionFloatingDialog(floating);

    return componentRef;
  }

  private positionFloatingDialog(floating: HTMLElement): void {
    const dialogRect = this.dialogRef().nativeElement.getBoundingClientRect();

    const GAP_X = 8;
    const GAP_Y = 64;

    Object.assign(floating.style, {
      position: 'fixed',
      margin: '0',
      left: `${dialogRect.right + GAP_X}px`,
      top: `${dialogRect.top + GAP_Y}px`,
    });
  }

  closeFloatingComponent(): void {
    const floating = this.floatingDialogRef().nativeElement;

    if (floating.matches(':popover-open')) {
      floating.hidePopover();
    }

    this.floatingComponentRef?.destroy();
    this.floatingComponentRef = undefined;

    floating.innerHTML = '';
  }

  private isInteractiveElement(element: HTMLElement): boolean {
    // Allow interaction with interactive elements
    // Check if target itself is an interactive element.
    // `[data-user-selectable]` is the opt-in dialog contents use to let the user select text:
    // without it the pointerdown preventDefault above (which exists to stop Edge from dragging
    // the dialog out of the top layer) also blocks the browser's text-selection gesture.
    return Boolean(
      element.closest('button, input, textarea, a, select, [data-user-selectable]')
        || element.isContentEditable,
    );
  }
}
